<script setup>
import { useBreakpoints } from '@daysmart/daysmart-vue-components/composables'
import fallbackImage from '@/appointment_types/assets/images/fallbackImage.png'

const props = defineProps({
  appointment: {
    type: Object,
    required: true,
  },
})
const { t } = useI18n()
const { current: currentBreakpoint } = useBreakpoints()

const uniqueInstructors = computed(() => {
  const appointmentInstructors = props.appointment.instructors
  return [...new Set(props.appointment.instructors.map(instructor => instructor.id))]
    .map(id => appointmentInstructors.find(instructor => instructor.id === id))
})
const isTabletView = computed(
  () => currentBreakpoint.value === 'md',
)
const isMobileView = computed(
  () => currentBreakpoint.value === 'sm' || currentBreakpoint.value === 'xs',
)
</script>

<template>
  <BaseLink
    :url="`/appointment_types/${appointment.id}`"
    :apply-current-provider-prefix="true"
    :v2-link="true"
  >
    <div
      v-if="appointment"
      class="mb-3 flex flex-row items-center gap-4 b-1 b-surface-600 b-rounded-2 p-6 shadow-sm lt-md:(flex-col) hover:(b-surface-700 shadow-md)"
    >
      <img
        :src="appointment.image ? appointment.image.url : fallbackImage"
        :alt="t('Venue image')"
        class="relative z-0 h-[276px] w-[348px] rounded-3 object-cover lt-md:(w-100%)"
      >
      <div class="mx-4 flex flex-1 flex-col gap-2 lt-md:(mx-0 mt-2)">
        <p class="text-2xl fw-600 font-sans lt-md:(text-lg)">
          {{ appointment.name }}
        </p>
        <div class="flex flex-row items-center gap-1 text-secondary">
          <DSIcon
            icon="i-far-clock"
            size="24"
            class="flex-self-center"
          />
          {{ appointment.appointment_settings.duration }} {{ t('min') }}
        </div>
        <div class="flex flex-row items-center gap-1 text-secondary">
          <DSIcon
            icon="i-far-map-location"
            size="24"
            class="flex-self-center"
          />
          {{ appointment.appointment_settings.default_venue.name }}
        </div>

        <div
          class="appointment-description my-4 w-auto flex text-sm text-text-400 lt-md:(my-2 flex-col overflow-auto text-xs fw-400)"
        >
          <div :class="isTabletView ? `w-70%` : `w-90%`">
            <DSLineClamp
              :max-lines="isTabletView ? 5 : 2"
              :line-height="25"
              :interactive="false"
              :open-label="t('Read More')"
              :close-label="t('Read Less')"
            >
              <p v-sanitize="appointment.description" />
            </DSLineClamp>
          </div>
          <p
            v-if="appointment.description.length >= 300"
            class="flex flex-items-end text-sm text-secondary-300 font-bold underline lt-md:(mt-2 text-xs)"
          >
            {{ t('Read More') }}
          </p>
        </div>

        <p class="text-text-400 lt-md:(text-xs fw-400)">
          {{ t('Book With') }}
        </p>
        <DSAvatarList
          v-if="appointment.instructors"
          :people="uniqueInstructors.map(instructor => ({
            ...instructor,
            imageUrl: instructor.picture_url,
          }))
          "
          :max="4"
          border
          size="sm"
          class="!pl-1.5 !pt-2"
        />
      </div>
      <BaseLink
        :url="`/appointment_types/${appointment.id}`"
        class="ml-auto btn-md px-4 py-2 uppercase lt-md:(mt-3 w-full text-center) btn-fill-primary"
        :apply-current-provider-prefix="true"
        :v2-link="true"
      >
        {{ t('Book Now') }}
      </BaseLink>
    </div>
  </BaseLink>
</template>

<style>
.appointment-description p a {
  color: var(--color-primary-500);
  text-decoration: underline;
}
</style>

<i18n>
{
  "en-us": {
    "Book Now": "Book Now",
    "Duration": "Duration",
    "Book with": "Book with",
    "min": "min",
    "Read More": "Read More",
    "Read Less": "Read Less",
    "Venue image": "Venue image",
    "Book With": "Book With"
  }
}
</i18n>

<i18n locale="de" lang="json">
{
  "Book Now": "Jetzt buchen",
  "Book With": "Buchen mit",
  "Book with": "Buchen mit",
  "Duration": "Dauer",
  "Read Less": "Weniger lesen",
  "Read More": "Mehr lesen",
  "Venue image": "Veranstaltungsbild",
  "min": "Min."
}
</i18n>

<i18n locale="es" lang="json">
{
  "Book Now": "Reservar ahora",
  "Book With": "Reservar con",
  "Book with": "Reservar con",
  "Duration": "Duración",
  "Read Less": "Leer menos",
  "Read More": "Leer más",
  "Venue image": "Imagen del lugar",
  "min": "min"
}
</i18n>

<i18n locale="fr" lang="json">
{
  "Book Now": "Réservez maintenant",
  "Book With": "Réserver avec",
  "Book with": "Réserver avec",
  "Duration": "Durée",
  "Read Less": "Lire moins",
  "Read More": "Lire la suite",
  "Venue image": "Image du lieu",
  "min": "min"
}
</i18n>
